import { Drawer, Primary, Text } from '@cinch-labs/design-system'
import {
  useSearchAlertFiltersFormatting,
  useSearchAlerts,
} from '../../../hooks'
import SearchAlertsForm from '../form'
import styles from './drawer.module.css'
import { useState } from 'react'
import { SearchAlertRequestBody } from '../../../hooks/use-search-alerts/use-search-alerts'
import { FilterItem } from '../../../hooks/use-search-alert-filters-formatting/use-search-alert-filters-formatting'

interface SearchAlertsDrawerProps {
  onClose: () => void
}

const SearchAlertsDrawer = ({ onClose }: SearchAlertsDrawerProps) => {
  const [emailFrequency, setEmailFrequency] =
    useState<SearchAlertRequestBody['sa_frequency']>()

  const { registerSearchAlert, fetchStatus } = useSearchAlerts()

  const formattedSearchAlertFilters = useSearchAlertFiltersFormatting()

  const isFetchSuccess = fetchStatus === 'success'

  const emailFrequencyText = emailFrequency === '1d' ? 'every day' : 'each week'

  const renderInitialDrawerContent = () => (
    <>
      <Text fontSizeFixed="m" className={styles.drawerBodyCopy}>
        Be the first to know when cars that match what you&apos;re looking for
        become available.
      </Text>

      <Text
        element="h2"
        fontSizeFixed="m"
        fontWeight="bold"
        className={styles.drawerHeading}
      >
        Your search alerts
      </Text>

      <Text fontSizeFixed="m" className={styles.drawerBodyCopy}>
        We&apos;ll send you email alerts for:
      </Text>

      <ul className={styles.drawerList}>
        {formattedSearchAlertFilters.map((item: FilterItem) => (
          <Text
            key={item.key}
            aria-label={item.display}
            className={styles.drawerListItem}
            element="li"
            fontSizeFixed="s"
          >
            {item.display}
          </Text>
        ))}
      </ul>

      <Text>These alerts will replace any you have set up previously.</Text>

      <SearchAlertsForm
        fetchStatus={fetchStatus}
        sendDataToCustomerIo={registerSearchAlert}
        onFrequencyChanged={setEmailFrequency}
      />
    </>
  )

  const renderSuccessDrawerContent = () => (
    <>
      <Text
        fontSizeFixed="m"
        className={styles.successText}
        aria-live="assertive"
      >
        Nice one! We&apos;ll email you a fresh list of cars {emailFrequencyText}{' '}
        if we find ones you&apos;re looking for — so you won&apos;t miss out.
      </Text>
      <Primary isFullWidth onClick={onClose}>
        Continue browsing cars
      </Primary>
    </>
  )

  const drawerHeader = isFetchSuccess
    ? 'Search alerts set up'
    : 'Set up search alerts'

  return (
    <Drawer header={drawerHeader} onClose={onClose}>
      {!isFetchSuccess && renderInitialDrawerContent()}
      {isFetchSuccess && renderSuccessDrawerContent()}
    </Drawer>
  )
}

export default SearchAlertsDrawer
